import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useToast } from '@/components/ui/Toast';
import { PlanUpgradeModal } from '@/components/ui/Modals/PlanUpgradeModal';
import {
  detectOnboardingProgress,
  setOnboardingStepSkipped,
  type OnboardingProgress,
} from '@/services/onboarding';
import {
  buildOnboardingState,
  isSkippable,
  newlyCompletedStep as findNewlyCompletedStep,
  type OnboardingState,
  type OnboardingStepState,
  type OnboardingVisibility,
} from '@/utils/onboardingStages';
import type { PlanId } from '@/constants/planLimits';

// ---------------------------------------------------------------------------
// The step list lives in `constants/onboardingSteps.ts` and the arithmetic in
// `utils/onboardingStages.ts`. This provider only holds state: the data-derived progress, the
// skipped set (applied optimistically), the step-complete toast, and the guide modal's open flag.
// ---------------------------------------------------------------------------

interface ToastStep {
  key: string;
  label: string;
  completedLabel: string;
}

interface OnboardingProgressContextType {
  progress: OnboardingProgress | null;
  /** Stages, current stage, next step, counts — see utils/onboardingStages. */
  guide: OnboardingState;
  allComplete: boolean;
  totalSteps: number;
  completedSteps: number;
  refreshProgress: () => void;
  /** Mark an optional step as not applying / undo that. No-op for a step without a skip label. */
  skipStep: (stepKey: string) => void;
  undoSkip: (stepKey: string) => void;
  /** The ONE way into a step: the guide row, the banner's Next link and the toast all use it. */
  openStep: (step: OnboardingStepState) => void;
  // Step-complete toast
  newlyCompletedStep: ToastStep | null;
  nextStep: OnboardingStepState | null;
  dismissBanner: () => void;
  // Checklist modal
  isChecklistOpen: boolean;
  openChecklist: () => void;
  closeChecklist: () => void;
}

// Session flag gating the guide's one-shot auto-open on the dashboard.
// Tenant-scoped because sessionStorage outlives logout/login within a tab.
export const onboardingModalShownKey = (tenantId: string) =>
  `prophitable_onboarding_modal_shown:${tenantId}`;

const EMPTY_GUIDE: OnboardingState = {
  stages: [],
  currentStage: null,
  nextStep: null,
  finished: 0,
  total: 0,
  allComplete: false,
};

const OnboardingProgressContext = createContext<OnboardingProgressContextType>({
  progress: null,
  guide: EMPTY_GUIDE,
  allComplete: false,
  totalSteps: 0,
  completedSteps: 0,
  refreshProgress: () => {},
  skipStep: () => {},
  undoSkip: () => {},
  openStep: () => {},
  newlyCompletedStep: null,
  nextStep: null,
  dismissBanner: () => {},
  isChecklistOpen: false,
  openChecklist: () => {},
  closeChecklist: () => {},
});

export const useOnboardingProgress = () => useContext(OnboardingProgressContext);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const OnboardingProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { tenant, user, isOwner } = useAuth();
  const ownerFlag = isOwner();
  const visibility = useFeatureVisibility();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  // Skips live in their own state so a click shows immediately; the server answer replaces it.
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [newlyCompletedStep, setNewlyCompletedStep] = useState<ToastStep | null>(null);
  const [toastNextStep, setToastNextStep] = useState<OnboardingStepState | null>(null);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<string | null>(null);

  const previousProgressRef = useRef<OnboardingProgress | null>(null);
  const autoDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingRef = useRef(false);
  // A refresh asked for while one is running must still happen — dropping it lost the update that
  // follows a save or a skip (session 331 review). One trailing re-run covers any number of asks.
  const rerunRequestedRef = useRef(false);

  const visibilityFlags = visibility as unknown as OnboardingVisibility;
  const plan = (tenant?.subscription_plan || 'starter') as PlanId;

  const guide = useMemo(
    () => (progress ? buildOnboardingState(progress, skipped, visibilityFlags, plan) : EMPTY_GUIDE),
    [progress, skipped, visibilityFlags, plan]
  );

  // ------ Toast auto-dismiss ------
  const dismissBanner = useCallback(() => {
    setNewlyCompletedStep(null);
    setToastNextStep(null);
    if (autoDismissTimer.current) {
      clearTimeout(autoDismissTimer.current);
      autoDismissTimer.current = null;
    }
  }, []);

  const startAutoDismiss = useCallback(() => {
    if (autoDismissTimer.current) clearTimeout(autoDismissTimer.current);
    autoDismissTimer.current = setTimeout(() => {
      setNewlyCompletedStep(null);
      setToastNextStep(null);
      autoDismissTimer.current = null;
    }, 15_000);
  }, []);

  useEffect(() => {
    return () => {
      if (autoDismissTimer.current) clearTimeout(autoDismissTimer.current);
    };
  }, []);

  // ------ Core refresh logic ------
  const doRefresh = useCallback(async () => {
    if (!ownerFlag || !visibility.isConfigured) return;
    if (isLoadingRef.current) {
      rerunRequestedRef.current = true;
      return;
    }
    isLoadingRef.current = true;

    try {
      do {
        rerunRequestedRef.current = false;
        const fresh = await detectOnboardingProgress(tenant, user?.id);
        const prev = previousProgressRef.current;

        // The toast reads DATA only: a skip never announces "Variants added".
        const completed = findNewlyCompletedStep(prev, fresh, visibilityFlags);
        if (completed) {
          const freshSkipped = new Set(fresh.skippedSteps);
          const after = buildOnboardingState(fresh, freshSkipped, visibilityFlags, plan);
          setNewlyCompletedStep({ key: completed.key, label: completed.label, completedLabel: completed.completedLabel });
          setToastNextStep(after.nextStep);
          startAutoDismiss();
        }

        previousProgressRef.current = fresh;
        setProgress(fresh);
        setSkipped(new Set(fresh.skippedSteps));
      } while (rerunRequestedRef.current);
    } finally {
      isLoadingRef.current = false;
    }
  }, [ownerFlag, tenant, user?.id, visibility.isConfigured, visibilityFlags, plan, startAutoDismiss]);

  // Initial load
  useEffect(() => {
    if (ownerFlag && visibility.isConfigured) {
      doRefresh();
    }
  }, [ownerFlag, visibility.isConfigured]); // eslint-disable-line react-hooks/exhaustive-deps

  // Public refresh (fire-and-forget, no await needed at call site)
  const refreshProgress = useCallback(() => {
    doRefresh();
  }, [doRefresh]);

  // ------ Skips (optimistic; the RPC is atomic on the server) ------
  const setStepSkipped = useCallback(
    async (stepKey: string, next: boolean) => {
      if (!isSkippable(stepKey)) return;
      const apply = (on: boolean) =>
        setSkipped((current) => {
          const copy = new Set(current);
          if (on) copy.add(stepKey);
          else copy.delete(stepKey);
          return copy;
        });
      apply(next);
      try {
        const stored = await setOnboardingStepSkipped(stepKey, next);
        setSkipped(new Set(stored));
      } catch (err) {
        console.error('Failed to update Getting Started step:', err);
        apply(!next);
        showToast({ status: 'error', message: "Couldn't save that change to your Getting Started guide. Try again." });
      }
    },
    [showToast]
  );

  const skipStep = useCallback((stepKey: string) => { void setStepSkipped(stepKey, true); }, [setStepSkipped]);
  const undoSkip = useCallback((stepKey: string) => { void setStepSkipped(stepKey, false); }, [setStepSkipped]);

  // ------ Checklist modal ------
  const openChecklist = useCallback(() => {
    setIsChecklistOpen(true);
    // Progress otherwise refreshes only on editor saves; list-page actions and imports have none.
    doRefresh();
  }, [doRefresh]);
  const closeChecklist = useCallback(() => {
    setIsChecklistOpen(false);
    // Mark the auto-open as acknowledged only when the user actually CLOSES
    // the guide — setting it at open time let transient dashboard mounts
    // (signup/payment redirects) consume the one-shot before the user ever
    // saw it. Tenant-scoped: sessionStorage survives logout/login in the
    // same tab, so an unscoped key set by one tenant suppressed the pop for
    // a fresh signup (found in P7 onboarding QA).
    if (tenant?.id) {
      sessionStorage.setItem(onboardingModalShownKey(tenant.id), 'true');
    }
  }, [tenant?.id]);

  // ------ The one way into a step ------
  const openStep = useCallback(
    (step: OnboardingStepState) => {
      dismissBanner();
      // The guide closes either way: stacked on it, the upgrade prompt sat beside the guide's own
      // undimmed rows and the two read as one surface (walk, session 331).
      closeChecklist();
      if (step.locked && step.def.planFeature) {
        // A lower plan: sell the feature rather than land on ProtectedRoute's full-page block (W8).
        setUpgradeFeature(step.def.planFeature);
        return;
      }
      navigate(step.route);
    },
    [closeChecklist, dismissBanner, navigate]
  );

  return (
    <OnboardingProgressContext.Provider
      value={{
        progress,
        guide,
        allComplete: guide.allComplete,
        totalSteps: guide.total,
        completedSteps: guide.finished,
        refreshProgress,
        skipStep,
        undoSkip,
        openStep,
        newlyCompletedStep,
        nextStep: toastNextStep,
        dismissBanner,
        isChecklistOpen,
        openChecklist,
        closeChecklist,
      }}
    >
      {children}
      <PlanUpgradeModal
        isOpen={upgradeFeature !== null}
        onClose={() => setUpgradeFeature(null)}
        featureKey={upgradeFeature ?? ''}
      />
    </OnboardingProgressContext.Provider>
  );
};
